import { useState, useEffect, useCallback } from 'react'
import { supabase } from '@/lib/supabase'
import type { News, Match, Video, Standing, Comment } from '@/lib/supabase'

const publishedNowFilter = () => {
  const now = new Date().toISOString()
  return `published_at.is.null,published_at.lte.${now}`
}

const formatArrayContainsFilter = (value: string) => {
  const escapedValue = value.replace(/"/g, '\\"')
  return `{"${escapedValue}"}`
}

export function useNews(options?: {
  category?: string
  limit?: number
  isFeatured?: boolean
  isHeadline?: boolean
  status?: string
}) {
  const [news, setNews] = useState<News[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  const fetchNews = useCallback(async () => {
    setLoading(true)
    let query = supabase
      .from('news')
      .select('*')
      .eq('status', 'publicado')
      .or(publishedNowFilter())
      .order('published_at', { ascending: false })

    if (options?.category) {
      query = query.or(`category.eq.${options.category},categories.cs.${formatArrayContainsFilter(options.category)}`)
    }
    if (options?.isFeatured !== undefined) {
      query = query.eq('is_featured', options.isFeatured)
    }
    if (options?.isHeadline !== undefined) {
      query = query.eq('is_headline', options.isHeadline)
    }
    if (options?.limit) {
      query = query.limit(options.limit)
    }

    const { data, error } = await query

    if (error) {
      setError(error.message)
    } else {
      setNews(data || [])
    }
    setLoading(false)
  }, [options?.category, options?.limit, options?.isFeatured, options?.isHeadline])

  useEffect(() => {
    fetchNews()
  }, [fetchNews])

  return { news, loading, error, refetch: fetchNews }
}

export function useNewsBySlug(slug: string) {
  const [news, setNews] = useState<News | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    async function fetchNews() {
      const { data, error } = await supabase
        .from('news')
        .select('*')
        .eq('slug', slug)
        .eq('status', 'publicado')
        .or(publishedNowFilter())
        .maybeSingle()

      if (error) {
        setError(error.message)
      } else {
        setNews(data)
      }
      setLoading(false)
    }

    if (slug) {
      fetchNews()
    }
  }, [slug])

  return { news, loading, error }
}

export function useMatches() {
  const [matches, setMatches] = useState<Match[]>([])
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    async function fetchMatches() {
      const { data } = await supabase
        .from('matches')
        .select('*')
        .order('match_date', { ascending: false })
      setMatches(data || [])
      setLoading(false)
    }
    fetchMatches()
  }, [])

  return { matches, loading }
}

export function useLiveMatches() {
  const [matches, setMatches] = useState<Match[]>([])
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    async function fetchLive() {
      const { data } = await supabase
        .from('matches')
        .select('*')
        .eq('is_live', true)
        .order('match_date', { ascending: false })
      setMatches(data || [])
      setLoading(false)
    }
    fetchLive()

    const channel = supabase
      .channel('live-matches')
      .on('postgres_changes', { event: '*', schema: 'public', table: 'matches', filter: 'is_live=eq.true' }, (payload) => {
        fetchLive()
      })
      .subscribe()

    return () => {
      supabase.removeChannel(channel)
    }
  }, [])

  return { matches, loading }
}

export function useVideos(limit?: number) {
  const [videos, setVideos] = useState<Video[]>([])
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    async function fetchVideos() {
      let query = supabase
        .from('videos')
        .select('*')
        .order('created_at', { ascending: false })
      if (limit) {
        query = query.limit(limit)
      }
      const { data } = await query
      setVideos(data || [])
      setLoading(false)
    }
    fetchVideos()
  }, [limit])

  return { videos, loading }
}

export function useStandings(competition: string) {
  const [standings, setStandings] = useState<Standing[]>([])
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    async function fetchStandings() {
      const { data } = await supabase
        .from('standings')
        .select('*')
        .eq('competition', competition)
        .order('position', { ascending: true })
      setStandings(data || [])
      setLoading(false)
    }
    fetchStandings()
  }, [competition])

  return { standings, loading }
}

export function useComments(newsId: string) {
  const [comments, setComments] = useState<Comment[]>([])
  const [loading, setLoading] = useState(true)

  async function fetchComments() {
    const { data } = await supabase
      .from('comments')
      .select('*')
      .eq('news_id', newsId)
      .eq('status', 'aprovado')
      .order('created_at', { ascending: true })
    setComments(data || [])
    setLoading(false)
  }

  useEffect(() => {
    if (newsId) {
      fetchComments()
    }
  }, [newsId])

  async function addComment(content: string, userId: string, userName: string, userAvatar?: string | null, parentId?: string | null) {
    const { data, error } = await supabase
      .from('comments')
      .insert({
        news_id: newsId,
        user_id: userId,
        user_name: userName,
        user_avatar: userAvatar,
        content,
        parent_id: parentId || null,
        status: 'pendente',
      })
      .select()
      .single()

    if (error) {
      console.error('Error adding comment:', error)
      return null
    }
    await fetchComments()
    return data
  }

  async function likeComment(commentId: string, userId: string) {
    const { error } = await supabase
      .from('comment_likes')
      .insert({ comment_id: commentId, user_id: userId })
    if (error) {
      console.error('Error liking comment:', error)
      return false
    }
    await supabase.rpc('increment_comment_likes', { comment_id: commentId })
    await fetchComments()
    return true
  }

  async function reportComment(commentId: string, userId: string, reason?: string) {
    const { error: updateError } = await supabase
      .from('comments')
      .update({ status: 'denunciado' })
      .eq('id', commentId)
    
    if (updateError) {
      console.error('Error reporting comment:', updateError)
      return false
    }
    
    await fetchComments()
    return true
  }

  return { comments, loading, addComment, likeComment, reportComment, refetch: fetchComments }
}
